import { TimeInput } from "@/components/app/TimeInput";
import { useEffect, useMemo, useState } from "react";
import { Camera, ImagePlus, Loader2, X, ChevronDown, RotateCcw, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, saveRecord } from "@/lib/api";
import { FORMS } from "@/lib/forms";
import { uploadFile, fileUrl } from "@/lib/image";
import { useGet, useSettings } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const inputCls = "w-full h-12 rounded-lg border border-input bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-stone-900/20";
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function Chips({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button type="button" key={o} data-testid={`${testid}-${slug(o)}`} onClick={() => onChange(value === o ? "" : o)}
          className={`min-h-[44px] px-4 rounded-full border text-sm transition-colors ${value === o ? "bg-stone-900 text-white border-stone-900" : "bg-white border-input text-stone-700 hover:bg-stone-50"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

export function PhotoField({ value = [], onChange, testid = "photo" }) {
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      const ids = [];
      for (const f of files) ids.push((await uploadFile(f)).id);
      onChange([...(value || []), ...ids]);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <label className="flex-1 min-h-[48px] rounded-lg border border-input bg-white flex items-center justify-center gap-2 text-sm font-medium cursor-pointer" data-testid={`${testid}-take`}>
          <Camera className="h-4 w-4" /> Take photo
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={add} />
        </label>
        <label className="flex-1 min-h-[48px] rounded-lg border border-input bg-white flex items-center justify-center gap-2 text-sm font-medium cursor-pointer" data-testid={`${testid}-choose`}>
          <ImagePlus className="h-4 w-4" /> Choose existing
          <input type="file" accept="image/*" multiple className="hidden" onChange={add} />
        </label>
      </div>
      {busy && <div className="text-xs text-stone-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Uploading…</div>}
      {!!value?.length && (
        <div className="flex gap-2 flex-wrap">
          {value.map((id) => (
            <div key={id} className="relative">
              <img src={fileUrl(id)} alt="" className="h-16 w-16 object-cover rounded-md border" />
              <button type="button" onClick={() => onChange(value.filter((x) => x !== id))} className="absolute -top-2 -right-2 bg-stone-900 text-white rounded-full p-0.5" aria-label="Remove photo"><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ f, value, set, vals, settings, users, employees, testid }) {
  const opts = f.options || settings[f.optionsFrom] || (f.optionsFrom === "all_areas" ? settings.areas : null) || [];
  const t = `${testid}-${f.name}`;
  if (f.type === "chips") return <Chips options={opts} value={value} onChange={set} testid={t} />;
  if (f.type === "select")
    return (
      <select data-testid={t} className={inputCls} value={value || ""} onChange={(e) => set(e.target.value)}>
        <option value="">Select…</option>
        {opts.map((o) => <option key={o}>{o}</option>)}
      </select>
    );
  if (f.type === "user")
    return (
      <select data-testid={t} className={inputCls} value={value || ""} onChange={(e) => set(e.target.value, { assigned_name: users.find((u) => u.id === e.target.value)?.name || "" })}>
        <option value="">Unassigned</option>
        {users.map((u) => <option key={u.id} value={u.id}>{`${u.name}${u.title ? ` — ${u.title}` : ""}`}</option>)}
      </select>
    );
  if (f.type === "employee")
    return (
      <select data-testid={t} className={inputCls} value={value || ""} onChange={(e) => set(e.target.value)}>
        <option value="">Select…</option>
        {employees.map((e) => <option key={e.id}>{e.name}</option>)}
      </select>
    );
  if (f.type === "textarea")
    return <textarea data-testid={t} rows={3} className={`${inputCls} h-auto py-3`} placeholder={f.placeholder} value={value || ""} onChange={(e) => set(e.target.value)} />;
  if (f.type === "checkbox")
    return (
      <label className="flex items-center gap-3 min-h-[48px] cursor-pointer">
        <input data-testid={t} type="checkbox" className="h-6 w-6 accent-stone-900" checked={!!value} onChange={(e) => set(e.target.checked)} />
        <span className="text-sm">{f.label}</span>
      </label>
    );
  if (f.type === "time") return <TimeInput data-testid={t} className={inputCls} value={value || ""} onChange={(e) => set(e.target.value)} />;
  if (f.type === "photo") return <PhotoField value={value} onChange={set} testid={t} />;
  if (f.type === "file") return <FileField vals={vals} set={set} testid={t} />;
  const recent = f.suggest ? JSON.parse(sessionStorage.getItem(`86d_recent_${f.suggest}`) || "[]") : [];
  return (
    <>
      <input data-testid={t} className={inputCls} type={f.type === "number" ? "number" : f.type} step={f.step} inputMode={f.type === "number" ? "decimal" : undefined}
        placeholder={f.placeholder} value={value ?? ""} list={f.suggest ? `dl-${f.suggest}` : undefined} onChange={(e) => set(e.target.value)} />
      {f.suggest && <datalist id={`dl-${f.suggest}`}>{recent.map((r) => <option key={r} value={r} />)}</datalist>}
      {f.suggest && !!recent.length && (
        <div className="flex gap-2 flex-wrap mt-2">
          {recent.slice(0, 5).map((r) => <button type="button" key={r} onClick={() => set(r)} className="text-xs px-3 py-2 rounded-full bg-[#F3EFE6] text-stone-700">{r}</button>)}
        </div>
      )}
    </>
  );
}

function FileField({ vals, set, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const up = await uploadFile(f);
      set(up.id, { file_id: up.id, content_type: up.content_type, name: vals.name || f.name });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label data-testid={testid} className="min-h-[48px] rounded-lg border border-dashed border-input bg-white flex items-center justify-center gap-2 text-sm cursor-pointer">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
      {vals.file_id ? "File attached — tap to replace" : "Upload a file (PDF, image, doc)"}
      <input type="file" className="hidden" onChange={pick} />
    </label>
  );
}

export function RecordForm({ formKey, form: custom, initial, onSaved, testid = "record-form", submitLabel }) {
  const form = custom || FORMS[formKey];
  const { settings } = useSettings();
  const needsUsers = form.fields.some((f) => f.type === "user");
  const needsEmp = form.fields.some((f) => f.type === "employee");
  const users = useGet(needsUsers ? "/users/lite" : null).data || [];
  const employees = useGet(needsEmp ? "/employees?limit=300&status=Active" : null).data?.items || [];
  const lastKey = `86d_last_${form.path}`;
  const defaults = useMemo(() => {
    const d = {};
    form.fields.forEach((f) => { if (f.default !== undefined) d[f.name] = typeof f.default === "function" ? f.default() : f.default; });
    return d;
  }, [form]);
  const [vals, setVals] = useState(() => ({ ...defaults, ...(initial || {}) }));
  const [more, setMore] = useState(false);
  const [state, setState] = useState("idle");
  useEffect(() => { setVals({ ...defaults, ...(initial || {}) }); }, [initial, defaults]);
  const last = !initial && form.remember ? JSON.parse(sessionStorage.getItem(lastKey) || "null") : null;
  const [errs, setErrs] = useState({});

  const submit = async (e) => {
    e.preventDefault();
    const missing = form.fields.filter((f) => f.required && (vals[f.name] === undefined || vals[f.name] === "" || (typeof vals[f.name] === "string" && !vals[f.name].trim())));
    setErrs(Object.fromEntries(missing.map((m) => [m.name, `${m.label} is required`])));
    if (missing.length) {
      document.getElementById(`${testid}-field-${missing[0].name}`)?.querySelector("input, select, textarea, button")?.focus();
      return toast.error(`Please fill in: ${missing.map((m) => m.label).join(", ")}`);
    }
    const body = Object.fromEntries(Object.entries(vals).map(([k, v]) => [k, v === "None" ? null : v]));
    setState("saving");
    try {
      let res;
      if (initial?.id) res = { data: (await api.patch(`${form.path}/${initial.id}`, body)).data };
      else res = form.queue ? await saveRecord(form.path, body) : { data: (await api.post(form.path, body)).data };
      if (form.remember) sessionStorage.setItem(lastKey, JSON.stringify(Object.fromEntries(form.remember.map((k) => [k, body[k]]))));
      form.fields.filter((f) => f.suggest && body[f.name]).forEach((f) => {
        const k = `86d_recent_${f.suggest}`;
        sessionStorage.setItem(k, JSON.stringify([body[f.name], ...JSON.parse(sessionStorage.getItem(k) || "[]").filter((x) => x !== body[f.name])].slice(0, 8)));
      });
      setState(res.queued ? "queued" : "saved");
      toast.success(res.queued ? "Saved on this device — will sync when you're back online" : `${form.title} saved`);
      onSaved?.(res.data);
    } catch (err) {
      setState("idle");
      toast.error(errMsg(err));
    }
  };

  const shown = form.fields.filter((f) => f.type !== "hidden" && (!f.more || more));
  const hasMore = form.fields.some((f) => f.more);
  return (
    <form onSubmit={submit} className="space-y-5" data-testid={testid}>
      {last && (
        <button type="button" data-testid={`${testid}-repeat-last`} onClick={() => setVals((v) => ({ ...v, ...last }))}
          className="w-full flex items-center justify-center gap-2 min-h-[44px] rounded-lg bg-[#F3EFE6] text-sm font-medium">
          <RotateCcw className="h-4 w-4" /> Repeat last: {Object.values(last).filter(Boolean).slice(0, 2).join(" · ")}
        </button>
      )}
      {shown.map((f) => (
        <div key={f.name} className="space-y-2" id={`${testid}-field-${f.name}`}>
          {f.type !== "checkbox" && <label className="text-sm font-medium text-stone-800">{f.label}{f.required && <span className="text-[#9E2A2B]"> *</span>}</label>}
          <Field f={f} value={vals[f.name]} vals={vals} settings={settings} users={users} employees={employees} testid={testid}
            set={(v, extra = {}) => { setVals((s) => ({ ...s, [f.name]: v, ...extra })); if (errs[f.name]) setErrs((e) => ({ ...e, [f.name]: undefined })); }} />
          {errs[f.name] && <p role="alert" className="text-sm text-[#9E2A2B]" data-testid={`${testid}-error-${f.name}`}>{errs[f.name]}</p>}
        </div>
      ))}
      {hasMore && (
        <button type="button" data-testid={`${testid}-more-toggle`} onClick={() => setMore(!more)} className="flex items-center gap-1 text-sm font-medium text-stone-600">
          <ChevronDown className={`h-4 w-4 transition-transform ${more ? "rotate-180" : ""}`} /> {more ? "Fewer details" : "Add more details"}
        </button>
      )}
      <div className="sticky bottom-0 bg-white pt-2 pb-1">
        <Button type="submit" data-testid={`${testid}-submit`} disabled={state === "saving"} className="w-full h-12 text-base rounded-lg">
          {state === "saving" ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…</> : submitLabel || (initial?.id ? "Save changes" : `Save ${form.title.toLowerCase()}`)}
        </Button>
      </div>
    </form>
  );
}
